// Scale-aware programmatic scrolling for the form view (task 1.3). The widget
// renders inside a transform:scale wrapper in production, where native
// `scrollIntoView({ behavior: 'smooth' })` misfires: getBoundingClientRect
// returns *visual* (post-transform) pixels while scrollTop/clientHeight are
// layout pixels, so the browser lands the scroll in the wrong place. We divide
// the scale factor out and drive scrollTop ourselves with rAF — a port of the
// prototype's efSmoothScrollTo/efScrollEl (app.jsx).

/** Anchor: the element's top lands this far below the scroller's top, keeping
 *  a tall field plus its help text readable (prototype metric). */
export const SCROLL_ANCHOR_OFFSET = 56;

/** Animation length of the eased scroll. */
export const SCROLL_DURATION_MS = 720;

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Clamp a target scrollTop to the scroller's real range. */
const clampTop = (scroller: HTMLElement, top: number): number =>
    Math.max(0, Math.min(top, scroller.scrollHeight - scroller.clientHeight));

/**
 * One in-flight animation per scroller — a new `smoothScrollTo` call for the
 * same scroller, or genuine user scroll input on it, cancels whatever run is
 * already in progress (its rAF loop AND its trailing timeout) rather than
 * fighting it. Without this, two calls in flight raced per-frame from stale
 * origins, and each call's own trailing timeout unconditionally forced its
 * possibly-stale target afterward — the mobile snap-back-away-from-the-
 * keyboard-focused-field symptom.
 */
const activeRuns = new WeakMap<HTMLElement, () => void>();

/** Cancel whatever `smoothScrollTo` run is in flight for `scroller`, if any. */
export const cancelSmoothScroll = (scroller: HTMLElement): void => {
    activeRuns.get(scroller)?.();
};

/** Events that unambiguously mean a person is scrolling this element by
 *  hand — never fired by our own `scrollTop` assignments below, so there is
 *  no risk of an animation cancelling itself. */
const USER_SCROLL_EVENTS: Array<keyof HTMLElementEventMap> = ['wheel', 'touchstart', 'pointerdown'];

/**
 * Animate `scroller.scrollTop` to `top` (clamped) over `duration` with an
 * ease-in-out curve. Under `prefers-reduced-motion: reduce` the jump is
 * instant. A trailing timeout guarantees the final position even when rAF is
 * throttled (e.g. a backgrounded iframe).
 */
export const smoothScrollTo = (
    scroller: HTMLElement,
    top: number,
    duration: number = SCROLL_DURATION_MS
): void => {
    // A new call for this scroller supersedes whatever was already animating
    // it. Cancelling before reading `scrollTop` below also means this call's
    // `start` is the real current position, not one a stale loop is still
    // mid-flight toward.
    cancelSmoothScroll(scroller);

    const target = clampTop(scroller, top);
    if (prefersReducedMotion()) {
        scroller.scrollTop = target;
        return;
    }
    const start = scroller.scrollTop;
    const diff = target - start;
    if (Math.abs(diff) < 2) return;

    let cancelled = false;
    let rafId: number | null = null;
    let timeoutId: number | null = null;

    const cancel = () => {
        if (cancelled) return;
        cancelled = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
        if (timeoutId !== null) window.clearTimeout(timeoutId);
        USER_SCROLL_EVENTS.forEach((type) => scroller.removeEventListener(type, cancel));
        if (activeRuns.get(scroller) === cancel) activeRuns.delete(scroller);
    };
    activeRuns.set(scroller, cancel);
    // A passive listener: we only ever read that the event happened, never
    // block it — cancelling this run must not cost the user's own scroll a
    // frame of latency.
    USER_SCROLL_EVENTS.forEach((type) => scroller.addEventListener(type, cancel, { passive: true }));

    const t0 = performance.now();
    const step = (now: number) => {
        if (cancelled) return;
        const p = Math.min(1, (now - t0) / duration);
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        scroller.scrollTop = start + diff * eased;
        if (p < 1) {
            rafId = requestAnimationFrame(step);
        } else {
            cancel(); // landed exactly on target — nothing left to guarantee
        }
    };
    // The trailing timeout is armed before the rAF loop starts so a step
    // that reaches p=1 synchronously (as a test double may) still finds it
    // in place to clear, rather than one scheduled a line later.
    timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        if (Math.abs(scroller.scrollTop - target) > 3) scroller.scrollTop = target;
        cancel();
    }, duration + 80);
    rafId = requestAnimationFrame(step);
};

export type ScrollAlign = 'start' | 'center';

/**
 * The layout-space scrollTop that places `el` at the anchor within `scroller`.
 * `start` anchors the element's top SCROLL_ANCHOR_OFFSET below the scroller's
 * top; `center` centres it in the viewport (never higher than the anchor —
 * an element taller than the viewport degrades to the start anchor).
 *
 * Scale awareness: both rects are visual pixels while clientHeight is layout
 * pixels, so their ratio is the widget's scale factor — visual distances are
 * divided by it before entering scrollTop space.
 */
export const scrollTargetFor = (
    scroller: HTMLElement,
    el: HTMLElement,
    align: ScrollAlign = 'start'
): number => {
    const sr = scroller.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const scale = sr.height / scroller.clientHeight || 1;
    const offset =
        align === 'center'
            ? Math.max((scroller.clientHeight - er.height / scale) / 2, SCROLL_ANCHOR_OFFSET)
            : SCROLL_ANCHOR_OFFSET;
    return clampTop(scroller, scroller.scrollTop + (er.top - sr.top) / scale - offset);
};

/** Smooth-scroll `el` to its anchored position within `scroller`. */
export const scrollToElement = (
    scroller: HTMLElement | null | undefined,
    el: HTMLElement | null | undefined,
    align: ScrollAlign = 'start'
): void => {
    if (!scroller || !el) return;
    smoothScrollTo(scroller, scrollTargetFor(scroller, el, align));
};
